// sim/territoryDriver.ts — the LIVE HEARTBEAT for the TerritorySystem.
//
// `TerritorySystem` (territory.ts) is a pure, observe-only ladder that turns reached
// regions into productive provinces when fed WORKER LABOR + FOOD THROUGHPUT, tracked
// PER-(COLONY, REGION). It ships "example-driven" — nothing drives it in a real world.
// This thin System closes that gap: on a fixed cadence it walks EVERY live colony,
// reads that colony's OWN establishment labor (workers + specialists) and its OWN
// food-deposit throughput (the delta since the last step), and pours them into
// `territory.investLabor(cid, …)` against a live `RegionManager` (the same regions the
// rival/region layer unlocks). So every colony GRADUALLY establishes the zones its
// frontier has reached — the player and its rivals each build their own empire.
//
// DRIVEN, NOT OWNED: it holds the TerritorySystem and a reference to the colony census +
// region manager, but it never mutates them — it only reads deterministic sim state. Being
// a `System` in the world's fixed-order loop makes it BATCH-INVARIANT (one `update` per
// tick regardless of step size), so the live game and a replay establish territory
// identically. Allocation-free per tick; integer-only; no RNG. Folds to a 32-bit hash.

import { CASTE } from '../types.ts';
import type { System, Tick, ColonyId } from '../types.ts';
import type { ColonySystem } from './colony.ts';
import type { RegionManager } from './regions.ts';
import { TerritorySystem, newTerritorySystem } from './territory.ts';
import type { TerritoryDecayConfig } from './territoryData.ts';

/** Tuning for the establishment heartbeat. */
export interface TerritoryDriverConfig {
  /** Ticks between establishment steps (one `investLabor` sweep per cadence). */
  readonly cadence: number;
  /** Optional abandonment-decay tuning passed to the TerritorySystem (default inert). */
  readonly decay?: TerritoryDecayConfig;
}

/** A calm default cadence (mirrors the capacity cadence — establishment is slow). */
export function defaultTerritoryDriverConfig(): TerritoryDriverConfig {
  return { cadence: 16 };
}

export class TerritoryDriverSystem implements System {
  readonly name = 'territory';

  /** The driven ladder (public so callers can read its stage accessors directly). */
  readonly territory: TerritorySystem;

  private readonly colonies: ColonySystem;
  private readonly regions: RegionManager;
  private readonly cadence: number;

  /**
   * Per-colony cumulative food-deposit watermark, so a colony's throughput is the
   * per-step delta of ITS OWN deposits. Sized to the colony store's capacity;
   * preallocated so the per-tick sweep never allocates.
   */
  private readonly lastDeposited: Int32Array;

  // --- Public counters (deterministic; folded into the hash). ---
  /** Establishment sweeps run (cadence ticks elapsed). */
  stepsTotal = 0;
  /** Stage advancements observed across all colonies and all steps. */
  advancesTotal = 0;

  constructor(
    colonies: ColonySystem,
    regions: RegionManager,
    config: TerritoryDriverConfig = defaultTerritoryDriverConfig(),
  ) {
    this.colonies = colonies;
    this.regions = regions;
    this.cadence = config.cadence > 0 ? (config.cadence | 0) : 1;
    const maxColonies = Math.max(1, colonies.capacity);
    this.territory = newTerritorySystem(maxColonies, config.decay);
    this.lastDeposited = new Int32Array(maxColonies);
  }

  update(tick: Tick): void {
    if (tick % this.cadence !== 0) return;
    // Drive every live colony into its OWN slice of the ladder, in fixed id order.
    const count = this.colonies.count;
    for (let cid = 0; cid < count; cid++) {
      if (!this.colonies.isAlive(cid)) {
        // A dead colony invests nothing; freeze its throughput watermark so a future
        // revival measures a delta from where it left off (deposits only ever grow).
        continue;
      }
      // Establishment labor = the bodies that build provinces: Workers + Specialists.
      const labor =
        this.colonies.casteCountAt(cid, CASTE.Worker) +
        this.colonies.casteCountAt(cid, CASTE.Specialist);
      // Food throughput = food THIS colony delivered home since its last step (≥ 0).
      const deposited = this.colonies.depositedByAt(cid);
      let throughput = deposited - (this.lastDeposited[cid] as number);
      if (throughput < 0) throughput = 0; // (defensive — deposits only grow)
      this.lastDeposited[cid] = deposited;

      this.advancesTotal += this.territory.investLabor(cid, this.regions, labor, throughput);
    }
    this.stepsTotal++;
  }

  /**
   * A single per-colony "established-territory" score for capacity/leaderboard consumers:
   * a colony's established provinces count once, its fully-integrated ones twice (a settled
   * empire supports more mouths). Each colony reads its OWN slice. Pure integer read.
   */
  establishmentScoreOf(colony: ColonyId): number {
    if (colony < 0 || colony >= this.territory.maxColonies) return 0;
    return this.territory.establishedCount(colony) + 2 * this.territory.integratedCount(colony);
  }

  /** FNV-1a fold of the full driver state (the ladder + the watermarks + counters). */
  hash(): number {
    let h = this.territory.hash() >>> 0;
    for (let i = 0; i < this.lastDeposited.length; i++) {
      h = Math.imul(h ^ (this.lastDeposited[i] as number | 0), 0x01000193) >>> 0;
    }
    h = Math.imul(h ^ (this.stepsTotal | 0), 0x01000193) >>> 0;
    h = Math.imul(h ^ (this.advancesTotal | 0), 0x01000193) >>> 0;
    return h >>> 0;
  }
}
